import * as DialogService from 'services/dialog';
import { ProductStateModule } from 'store';
import { hasRetryablePhotoErrors } from 'utils/photo-processing-error';
import UploadErrorsView from './index.vue';

interface UploadErrorsDialogOptions {
	/**
	 * Offer "choose other photos": remove the failed photos and stay, so the
	 * user can add others instead. Only for flows that can add photos in place.
	 */
	canReplace?: boolean;
	/**
	 * Continues the flow after the failed photos were retried or removed.
	 */
	onContinue: () => void;
}

/**
 * Remove every failed photo from the product.
 */
function removeFailedPhotos(): void {
	ProductStateModule.getPhotosFailed
		.slice(0)
		.forEach((photoModel) => {
			ProductStateModule.removePhoto(photoModel.id);
		});
}

/**
 * Show the failed uploads of the current product, with retry (when any
 * failure is retryable), replace (when the caller allows it) and remove.
 * The dialog closes before any action runs.
 *
 * @param options Dialog options.
 * @returns A function that closes the dialog without taking any action.
 */
export function openUploadErrorsDialog({
	canReplace,
	onContinue,
}: UploadErrorsDialogOptions): () => void {
	const closeError = DialogService.openErrorDialog({
		header: {
			hasCloseButton: false,
		},
		body: {
			component: UploadErrorsView,
		},
		footer: {
			buttons: [
				...(
					hasRetryablePhotoErrors(ProductStateModule.getPhotosFailed)
						? [
							{
								id: 'abort',
								text: window.App.router.$t('dialogButtonUploadErrorsCancel'),
								click: () => {
									closeError();

									const closeLoader = DialogService.openLoaderDialog();

									ProductStateModule
										.retryPhotoErrors({
											reset: true,
										})
										.catch(() => {
											/** Swallow error: no action required. */
										})
										.finally(() => {
											closeLoader();
											onContinue();
										});
								},
							},
						]
						: []
				),
				...(
					canReplace
						? [
							{
								id: 'replace',
								text: window.App.router.$t('dialogButtonUploadErrorsReplace'),
								click: () => {
									closeError();
									removeFailedPhotos();
								},
							},
						]
						: []
				),
				{
					id: 'accept',
					text: window.App.router.$t('dialogButtonUploadErrorsOk'),
					click: () => {
						closeError();
						removeFailedPhotos();
						onContinue();
					},
				},
			],
		},
		width: 300,
	});

	return closeError;
}
